<template>
  <div>
    <svg viewBox="0 0 300 430" :aria-label="$t('coach.hitgroup.figure_label')">
      <path
        v-for="region in limbRegions"
        :key="region.key"
        class="hg-region hg-limb"
        :class="{ 'hg-hot': hotKey === region.key, 'hg-nodata': isEmpty(region) }"
        :d="LIMB_PATHS[region.key]"
        fill="none"
        stroke-width="19"
        stroke-linecap="round"
        :stroke="isEmpty(region) ? undefined : fillFor(region)"
        @mouseenter="onEnter(region, $event)"
        @mousemove="onMove(region, $event)"
        @mouseleave="onLeave"
      />
      <path
        v-for="region in bodyRegions"
        :key="region.key"
        class="hg-region"
        :class="{ 'hg-hot': hotKey === region.key, 'hg-nodata': isEmpty(region) }"
        :d="BODY_PATHS[region.key]"
        :fill="isEmpty(region) ? undefined : fillFor(region)"
        @mouseenter="onEnter(region, $event)"
        @mousemove="onMove(region, $event)"
        @mouseleave="onLeave"
      />

      <text v-for="label in INSIDE_LABELS" :key="label.key" class="hg-label" :x="label.x" :y="label.y" :style="labelStyle(label.key)">
        {{ labelText(label.key) }}
      </text>
      <template v-for="label in OUTSIDE_LABELS" :key="label.key">
        <line class="hg-leader" :x1="label.x1" :y1="label.y1" :x2="label.x2" :y2="label.y2" />
        <text class="hg-label hg-label-outside" :x="label.x" :y="label.y">{{ labelText(label.key) }}</text>
      </template>
    </svg>

    <Teleport to="body">
      <div v-if="tip" class="hg-tip" :style="{ left: `${tip.left}px`, top: `${tip.top}px` }" role="tooltip">
        <div class="flex items-baseline justify-between font-semibold mb-1.5">
          <span>{{ tip.region.label }}</span>
          <span class="tabular-nums">{{ shareOf(tip.region)?.toFixed(1) }}%</span>
        </div>
        <table class="w-full text-xs">
          <tbody>
            <tr><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.hits') }}</td><td class="hg-num">{{ tip.region.hitCount }} ({{ tip.region.hitShare?.toFixed(1) }}%)</td></tr>
            <tr><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.damage') }}</td><td class="hg-num">{{ tip.region.damage }} ({{ tip.region.damageShare?.toFixed(1) ?? '0.0' }}%)</td></tr>
            <tr v-if="tip.region.peerShare != null"><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.bracket_hit_share') }}</td><td class="hg-num">{{ tip.region.peerShare.toFixed(1) }}%</td></tr>
            <tr v-if="tipDelta"><td class="text-gray-400 py-0.5">{{ $t('coach.hitgroup.vs_bracket') }}</td><td class="hg-num" :style="{ color: DELTA_COLORS[tipDelta.tone] }">{{ tipDelta.diff >= 0 ? '+' : '−' }}{{ Math.abs(tipDelta.diff).toFixed(1) }}</td></tr>
          </tbody>
        </table>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { regionDelta } from '../../utils/hitgroups.js';

const props = defineProps({
  regions: { type: Array, required: true },
  measure: { type: String, default: 'hits' }, // 'hits' | 'damage'
});

const LIMB_PATHS = {
  left_arm: 'M100 110 L88 216',
  right_arm: 'M200 110 L212 216',
};
const BODY_PATHS = {
  // plain circle - neck hits are folded into the head
  head: 'M150 35 a27 27 0 1 0 0.02 0 Z',
  chest: 'M126 96 L174 96 Q193 100 191 120 L188 168 L112 168 L109 120 Q107 100 126 96 Z',
  stomach: 'M112 172 L188 172 L191 204 Q193 228 170 230 L130 230 Q107 228 109 204 Z',
  left_leg: 'M109 245 Q109 236 118 236 L138 236 Q147 236 147 244 C147 300 146 348 144 390 Q144 400 134 400 L122 400 Q113 400 113 390 C110 348 109 300 109 245 Z',
  right_leg: 'M153 244 Q153 236 162 236 L182 236 Q191 236 191 245 C191 300 190 348 187 390 Q187 400 178 400 L166 400 Q156 400 156 390 C154 348 153 300 153 244 Z',
};
const INSIDE_LABELS = [
  { key: 'head', x: 150, y: 66 },
  { key: 'chest', x: 150, y: 136 },
  { key: 'stomach', x: 150, y: 204 },
  { key: 'left_leg', x: 126, y: 326 },
  { key: 'right_leg', x: 174, y: 326 },
];
const OUTSIDE_LABELS = [
  {
    key: 'left_arm', x1: 90, y1: 196, x2: 52, y2: 210, x: 44, y: 227,
  },
  {
    key: 'right_arm', x1: 210, y1: 196, x2: 248, y2: 210, x: 254, y: 227,
  },
];
// Refrag primary ramp: magnitude, deliberately not the success teal.
const RAMP_LOW = [33, 36, 105];
const RAMP_HIGH = [166, 169, 227];
const DELTA_COLORS = { good: '#25E5CE', bad: '#E4566A', flat: '#597497' };

const hotKey = ref(null);
const tip = ref(null);

const byKey = computed(() => Object.fromEntries(props.regions.map((r) => [r.key, r])));
const limbRegions = computed(() => props.regions.filter((r) => r.key.endsWith('_arm')));
const bodyRegions = computed(() => props.regions.filter((r) => !r.key.endsWith('_arm')));

function shareOf(region) {
  return props.measure === 'damage' ? region.damageShare : region.hitShare;
}
const maxShare = computed(() => Math.max(...props.regions.map((r) => shareOf(r) ?? 0), 0));

function isEmpty(region) {
  return !((props.measure === 'damage' ? region.damage : region.hitCount) > 0);
}

function fillFor(region) {
  const t = maxShare.value === 0 ? 0 : Math.min(1, (shareOf(region) ?? 0) / maxShare.value);
  const c = RAMP_LOW.map((low, i) => Math.round(low + (RAMP_HIGH[i] - low) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

function labelText(key) {
  const region = byKey.value[key];
  if (!region) return '';
  return isEmpty(region) ? '0' : `${Math.round(shareOf(region) ?? 0)}%`;
}

// Bright fills flip the label to dark ink for contrast.
function labelStyle(key) {
  const region = byKey.value[key];
  if (!region || isEmpty(region)) return {};
  const t = maxShare.value === 0 ? 0 : (shareOf(region) ?? 0) / maxShare.value;
  return t > 0.55 ? { fill: '#111336' } : {};
}

const tipDelta = computed(() => (tip.value ? regionDelta(tip.value.region) : null));

function place(event) {
  const width = 240;
  return {
    left: Math.max(10, Math.min(event.clientX - width / 2, window.innerWidth - width - 10)),
    top: Math.max(10, event.clientY - 14),
  };
}

function onEnter(region, event) {
  hotKey.value = region.key;
  tip.value = { region, ...place(event) };
}

function onMove(region, event) {
  if (tip.value) tip.value = { region, ...place(event) };
}

function onLeave() {
  hotKey.value = null;
  tip.value = null;
}
</script>

<style scoped>
svg { width: 100%; height: auto; display: block; }
.hg-region { cursor: pointer; transition: filter 0.12s ease; }
.hg-region:not(.hg-limb) { stroke: #080a0d; stroke-width: 2; }
.hg-hot { filter: brightness(1.3); }
.hg-region.hg-nodata:not(.hg-limb) { fill: #161e27; stroke: #37485d; stroke-dasharray: 3 3; }
.hg-limb.hg-nodata { stroke: #161e27; stroke-dasharray: 3 5; }
.hg-label {
  font-family: ui-monospace, monospace; font-size: 15px; font-weight: 600;
  fill: #eff2f5; text-anchor: middle; pointer-events: none;
}
.hg-label-outside { fill: #9cadc4; }
.hg-leader { stroke: #37485d; stroke-width: 1; }
@media (prefers-reduced-motion: reduce) { .hg-region { transition: none; } }
</style>

<style>
.hg-tip {
  position: fixed; z-index: 10000; width: 240px; transform: translateY(-100%);
  background: #131a22; border: 1px solid #263243; border-radius: 8px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.55); padding: 12px 14px;
  font-size: 12.5px; color: #eff2f5; pointer-events: none;
}
.hg-tip .hg-num {
  text-align: right; font-family: ui-monospace, monospace;
  font-variant-numeric: tabular-nums; color: #eff2f5;
}
</style>
